import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Avatar,
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonIcon from '@mui/icons-material/Person';

import { fetchTeamStandings } from './StandingsApi.jsx';

const getTeamPhoto = (team) => {
  if (team?.logoUrl) return team.logoUrl;
  return '';
};

const buildTeamUrl = (team) => {
  if (!team?._id) return null;

  const teamLabel = team?.name || team?.fullName || team?._id;
  return `/dsas-view/equipe/${encodeURIComponent(teamLabel)}_${team._id}`;
};

const formatInteger = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '-';
  return new Intl.NumberFormat('fr-FR', {
    maximumFractionDigits: 0,
  }).format(value);
};

const isRugbyDiscipline = (discipline) => discipline === 'rugby';

const getScoredLabel = (discipline) =>
  isRugbyDiscipline(discipline) ? 'BP' : 'BP';

const getConcededLabel = (discipline) =>
  isRugbyDiscipline(discipline) ? 'BC' : 'BC';

const getDiffLabel = () => '+/-';

const getScoredValue = (item, discipline) =>
  isRugbyDiscipline(discipline) ? item.goalsScored : item.goalsScored;

const getConcededValue = (item, discipline) =>
  isRugbyDiscipline(discipline) ? item.goalsConceded : item.goalsConceded;

const getDiffValue = (item, discipline) =>
  isRugbyDiscipline(discipline)
    ? item.goalDiff
    : item.goalDiff;

export default function StandingsSection({
  discipline,
  competitionIds = [],
  season,
  day = null,
  statType = 'general',
  groupKey = null,
  widgetConfigurationId = null,
  onNavigate,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState({
    items: [],
  });

  const competitionKey = useMemo(
    () => competitionIds?.join(',') || '',
    [competitionIds]
  );

  const tableScrollRef = useRef(null);

  const [tableScrollState, setTableScrollState] = useState({
    canScroll: false,
    canScrollLeft: false,
    canScrollRight: false,
  });

  const updateTableScrollState = useCallback(() => {
    const element = tableScrollRef.current;
    if (!element) return;

    const maxScrollLeft = element.scrollWidth - element.clientWidth;
    const scrollLeft = element.scrollLeft;

    setTableScrollState({
      canScroll: maxScrollLeft > 2,
      canScrollLeft: scrollLeft > 2,
      canScrollRight: scrollLeft < maxScrollLeft - 2,
    });
  }, []);

  useEffect(() => {
  updateTableScrollState();

  const timeoutId = window.setTimeout(updateTableScrollState, 0);

  const handleResize = () => updateTableScrollState();
  window.addEventListener('resize', handleResize);

  return () => {
    window.clearTimeout(timeoutId);
    window.removeEventListener('resize', handleResize);
  };
}, [data.items, updateTableScrollState]);

  const getAverageStatsColumnWidth = () => {
  const element = tableScrollRef.current;
  if (!element) return 56;

  const headerCells = Array.from(
    element.querySelectorAll('.standings-table thead th')
  );

  // On ignore les 2 colonnes sticky : # + Équipe
  const statsCells = headerCells.slice(2);

  if (!statsCells.length) return 56;

  const totalWidth = statsCells.reduce(
    (sum, cell) => sum + cell.getBoundingClientRect().width,
    0
  );

  return Math.max(42, Math.round(totalWidth / statsCells.length));
};

const handleTableSlide = (direction) => {
  const element = tableScrollRef.current;
  if (!element) return;

  const maxScrollLeft = element.scrollWidth - element.clientWidth;
  const averageColumnWidth = getAverageStatsColumnWidth();

  const nextScrollLeft = Math.min(
    Math.max(element.scrollLeft + direction * averageColumnWidth, 0),
    maxScrollLeft
  );

  element.scrollTo({
    left: nextScrollLeft,
    behavior: 'smooth',
  });

  window.setTimeout(updateTableScrollState, 220);
};

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      try {
        setLoading(true);
        setError('');

        const result = await fetchTeamStandings({
          discipline,
          competitionIds,
          season,
          widgetConfigurationId,
          day,
          statType,
          groupKey,
        });

        if (!ignore) {
          setData({
            items: Array.isArray(result?.items) ? result.items : [],
          });
        }
      } catch (e) {
        if (!ignore) {
          setError('Impossible de charger le classement.');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      ignore = true;
    };
  }, [discipline, competitionKey, season, day, statType, groupKey]);

  const handleLinkClick = (event, url) => {
    if (!url || !onNavigate) return;
    event.preventDefault();
    onNavigate(url);
  };

  const renderTable = () => (
  <Box className="standings-table-shell">
    {tableScrollState.canScroll ? (
      <Box className="standings-table-slide-controls">
        <IconButton
          size="small"
          onClick={() => handleTableSlide(-1)}
          disabled={!tableScrollState.canScrollLeft}
          className="standings-table-slide-button"
          aria-label="Afficher les colonnes précédentes"
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>

        <IconButton
          size="small"
          onClick={() => handleTableSlide(1)}
          disabled={!tableScrollState.canScrollRight}
          className="standings-table-slide-button"
          aria-label="Afficher les colonnes suivantes"
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>
    ) : null}

    <TableContainer
      ref={tableScrollRef}
      onScroll={updateTableScrollState}
      className="standings-table-wrap"
    >
      <Table size="small" className="standings-table">
        <TableHead>
          <TableRow>
            <TableCell className="standings-table__rank-cell">#</TableCell>
            <TableCell className="standings-table__team-cell">Équipe</TableCell>
            <TableCell align="center" className="standings-table__points-header">
              Points
            </TableCell>
            <TableCell align="center" className="standings-table__points-header">
              MJ
            </TableCell>
            <TableCell align="center" className="standings-table__points-header">
              G
            </TableCell>
            <TableCell align="center" className="standings-table__points-header">
              N
            </TableCell>
            <TableCell align="center" className="standings-table__points-header">
              P
            </TableCell>
            <TableCell align="center" className="standings-table__points-header">
              {getScoredLabel(discipline)}
            </TableCell>
            <TableCell align="center" className="standings-table__points-header">
              {getConcededLabel(discipline)}
            </TableCell>

            {discipline === 'rugby' ? (
              <>
                <TableCell align="center" className="standings-table__points-header">
                  BO
                </TableCell>
                <TableCell align="center" className="standings-table__points-header">
                  BD
                </TableCell>
              </>
            ) : null}

            <TableCell align="center">{getDiffLabel()}</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {data.items.map((item, index) => {
            const teamUrl = buildTeamUrl(item.team);

            return (
              <TableRow
                key={item.team?._id || `${statType}-${index}`}
                hover
                className="standings-table__row"
              >
                <TableCell className="standings-table__rank-cell">
                  {item.rank ?? index + 1}
                </TableCell>

                <TableCell className="standings-table__team-cell">
                  <Stack direction="row" alignItems="center" className="standings-table__team">
                    <Avatar
                      src={getTeamPhoto(item.team)}
                      alt={item.team?.name || 'team'}
                      className="standings-table__team-logo"
                    >
                      <PersonIcon fontSize="small" />
                    </Avatar>

                    <Box className="standings-table__team-text">
                      <Typography variant="body2" className="standings-table__team-name">
                        {teamUrl ? (
                          <Link
                            href={teamUrl}
                            underline="hover"
                            color="inherit"
                            onClick={(e) => handleLinkClick(e, teamUrl)}
                            className="standings-table__team-link"
                          >
                            {item.team?.name || item.team?.fullName || '-'}
                          </Link>
                        ) : (
                          item.team?.name || item.team?.fullName || '-'
                        )}
                      </Typography>

                      {item.team?.fullName &&
                      item.team?.fullName !== item.team?.name ? (
                        <Typography variant="caption" className="standings-table__team-fullname">
                          {item.team.fullName}
                        </Typography>
                      ) : null}
                    </Box>
                  </Stack>
                </TableCell>

                <TableCell align="right" className="standings-table__points-cell">
                  <span className="standings-table__points-pill">
                    {formatInteger(item.points)}
                  </span>
                </TableCell>

                <TableCell align="right">{formatInteger(item.played)}</TableCell>
                <TableCell align="right">{formatInteger(item.won)}</TableCell>
                <TableCell align="right">{formatInteger(item.draw)}</TableCell>
                <TableCell align="right">{formatInteger(item.lost)}</TableCell>

                <TableCell align="right">
                  {formatInteger(getScoredValue(item, discipline))}
                </TableCell>

                <TableCell align="right">
                  {formatInteger(getConcededValue(item, discipline))}
                </TableCell>

                {discipline === 'rugby' ? (
                  <>
                    <TableCell align="right">{formatInteger(item.BO)}</TableCell>
                    <TableCell align="right">{formatInteger(item.BD)}</TableCell>
                  </>
                ) : null}

                <TableCell align="right" className="standings-table__diff-cell">
                  {formatInteger(getDiffValue(item, discipline))}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

  if (loading) {
    return (
      <Box className="standings-section__loading">
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data.items.length) {
    return (
      <Alert severity="info">
        Aucun classement disponible pour cette sélection.
      </Alert>
    );
  }

  return renderTable();
}
